import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {
    useMultiStyleConfig,
    Text,
    Flex,
    Heading,
    Img,
    Accordion,
    AccordionItem,
    AccordionPanel,
    AccordionButton,
    Box,
    useMediaQuery,
} from '@chakra-ui/react'
import {FormattedMessage} from 'react-intl'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {MinusIcon, PlusIcon} from '../../../components/icons'
import {getDamBaseUrl} from '../../../custom-hooks/use-dam-image'
import useSlideUpAnimation from '../../../custom-hooks/use-slide-up-animation'
import DynamicIcon from '../../../components/dynamic-icon/DynamicIcon'

// Shared easing for scroll-reveal, image crossfade and accordion motion so they feel consistent
const EASE_OUT = [0.22, 1, 0.36, 1]
const EASE_OUT_CSS = 'cubic-bezier(0.22, 1, 0.36, 1)'

const ACCORDION_MOTION_PROPS = {
    transition: {
        enter: {duration: 0.45, ease: EASE_OUT},
        exit: {duration: 0.3, ease: [0.4, 0, 1, 1]},
    },
}

// Stacks the previous and next image so the swap crossfades/scales in instead of hard-cutting
const FadeImage = ({src, isTop, onFadeInEnd}) => {
    const [visible, setVisible] = useState(false)

    useEffect(() => {
        const raf = requestAnimationFrame(() => setVisible(true))
        return () => cancelAnimationFrame(raf)
    }, [])

    return (
        <Img
            src={src}
            alt="Benefits"
            position="absolute"
            inset={0}
            w="100%"
            h="100%"
            objectFit="cover"
            opacity={visible ? 1 : 0}
            transform={visible ? 'scale(1)' : 'scale(1.04)'}
            transition={`opacity 0.5s ${EASE_OUT_CSS}, transform 0.6s ${EASE_OUT_CSS}`}
            onTransitionEnd={isTop ? onFadeInEnd : undefined}
        />
    )
}

FadeImage.propTypes = {
    src: PropTypes.string.isRequired,
    isTop: PropTypes.bool.isRequired,
    onFadeInEnd: PropTypes.func,
}

const BenefitsPDP = ({benefits}) => {
    const styles = useMultiStyleConfig('BenefitsPDP')
    const [isDesktop] = useMediaQuery('(min-width: 1024px)', {fallback: true})
    const isMobile = !isDesktop
    const {containerRef, getAnimProps} = useSlideUpAnimation()

    const baseUrl = getDamBaseUrl()

    const imgSrcFromDam = `${baseUrl}/${benefits[0]?.imageLink}`
    const initialImgSrc = imgSrcFromDam || getAssetUrl('static/img/404Images/404Image-delonghi.png')

    const [layers, setLayers] = useState([{src: initialImgSrc, key: 0}])
    const nextLayerKey = useRef(1)

    const changeImage = (newSrc) => {
        setLayers((prev) => {
            if (prev[prev.length - 1].src === newSrc) return prev
            const key = nextLayerKey.current++
            return [...prev, {src: newSrc, key}]
        })
    }

    const handleFadeInEnd = (key) => {
        setLayers((prev) => (prev.length > 1 ? prev.filter((layer) => layer.key === key) : prev))
    }

    const renderImage = () => (
        <Box position="relative" {...styles.image} overflow="hidden">
            {layers.map((layer, index) => (
                <FadeImage
                    key={layer.key}
                    src={layer.src}
                    isTop={index === layers.length - 1}
                    onFadeInEnd={() => handleFadeInEnd(layer.key)}
                />
            ))}
        </Box>
    )

    return (
        <Flex {...styles.outerContainer} data-testid="benefits-pdp-outer-container">
            <Flex {...styles.container}>
                <Flex {...(!isMobile ? styles.leftContainer : styles.mobileContainer)}>
                    <Flex {...styles.headerGrid}>
                        <Heading {...styles.title}>
                            <FormattedMessage
                                id="benefits_pdp.labels.title"
                                defaultMessage="Product Details"
                            />
                        </Heading>
                        <Text {...styles.description}>
                            <FormattedMessage
                                id="benefits_pdp.labels.description"
                                defaultMessage="What makes this special"
                            />
                        </Text>
                    </Flex>

                    {isMobile && renderImage()}

                    <Accordion ref={containerRef} defaultIndex={0} {...styles.benefitsContainer}>
                        {benefits.map((benefit, index) => (
                            <Box key={index} {...getAnimProps(index)}>
                                <AccordionItem {...styles.benefitItem}>
                                    {({isExpanded}) => (
                                        <>
                                            <AccordionButton
                                                {...styles.benefitAccordionButtonContainer}
                                                onClick={() => {
                                                    if (!isExpanded) {
                                                        changeImage(
                                                            `${baseUrl}/${benefit.imageLink}`,
                                                        )
                                                    }
                                                }}
                                            >
                                                <Flex {...styles.benefitIconAndTitleContainer}>
                                                    <DynamicIcon
                                                        iconId={benefit.code}
                                                        {...styles.benefitIcon}
                                                    />
                                                    <Text {...styles.benefitTitle}>
                                                        {benefit.title}
                                                    </Text>
                                                </Flex>

                                                <Box
                                                    transform={
                                                        isExpanded
                                                            ? 'rotate(180deg)'
                                                            : 'rotate(0deg)'
                                                    }
                                                    {...styles.iconBox}
                                                >
                                                    {!isExpanded ? (
                                                        <PlusIcon
                                                            {...styles.benefitAccordionButton}
                                                        />
                                                    ) : (
                                                        <MinusIcon
                                                            {...styles.benefitAccordionButton}
                                                        />
                                                    )}
                                                </Box>
                                            </AccordionButton>

                                            <AccordionPanel
                                                {...styles.benefitPanel}
                                                motionProps={ACCORDION_MOTION_PROPS}
                                            >
                                                <Text {...styles.benefitDescription}>
                                                    {benefit.description}
                                                </Text>
                                            </AccordionPanel>
                                        </>
                                    )}
                                </AccordionItem>
                            </Box>
                        ))}
                    </Accordion>
                </Flex>

                {!isMobile && renderImage()}
            </Flex>
        </Flex>
    )
}

export default BenefitsPDP

BenefitsPDP.propTypes = {
    benefits: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string,
            description: PropTypes.string,
            imageLink: PropTypes.string,
        }),
    ).isRequired,
}
